import { ElementType, FC } from 'react';
import Icon from '../Icon';
import Opportunity from '../types/Opportunity';
import cn from '../Utils/cn';
import Link from 'next/link';
import Image from 'next/image';

type Props = {
	as?: ElementType;
	className?: string;
	href?: string;
	companyLogo?: string;
	variant?: 'default' | 'search';
} & Opportunity;

const degreeMap = {
	'Doutorado Completo': 'Doutorado Completo',
	'Doutorado Incompleto': 'Doutorado Incompleto',
	'Mestrado Completo': 'Mestrado Completo',
	'Mestrado Incompleto': 'Mestrado Incompleto',
	'Pós-Graduação Completa': 'Pós-Graduação Completa',
	'Pós-Graduação Incompleta': 'Pós-Graduação Incompleta',
	'Ensino Superior Completo': 'Ens. Superior Completo',
	'Ensino Superior Incompleto': 'Ens. Superior Incompleto',
	'Ensino Técnico Completo': 'Ens. Técnico Completo',
	'Ensino Técnico Incompleto': 'Ens. Técnico Incompleto',
	'Ensino Médio Completo': 'Ens. Médio Completo',
	'Ensino Médio Incompleto': 'Ens. Médio Incompleto',
	'Ensino Fundamental Completo': 'Ens. Fund. Completo',
	'Ensino Fundamental Incompleto': 'Ens. Fund. Incompleto',
};

const OpportunityCard: FC<Props> = ({ cod, jobTitle, city, edRequirement, sector, href, className, companyLogo, variant = 'default' }) => {
	const isSearchVariant = variant === 'search';
	const metadataItemClassName = cn(
		'flex items-center gap-2 leading-none font-semibold text-gray-600',
		isSearchVariant ? 'w-fit rounded-full bg-base-grey-2 px-4 py-3 text-sm' : 'text-xs',
	);

	return (
		<Link
			href={href || '/'}
			className={cn(
				'w-full',
				isSearchVariant ? 'flex flex-col items-start gap-8 px-8 py-8 group' : 'flex flex-col items-start gap-4 px-6 py-6 group',
				'bg-white rounded-[24px] text-base-grey-5', 
				'border border-solid border-transparent hover:border-[#0F1DD7] transition-all duration-300 shadow-md hover:shadow-lg',
				className,
			)}
			// Sem aria-label fixo: o nome acessível passa a ser o conteúdo do card
			// (título da vaga etc.), permitindo distinguir os cards em leitores de
			// tela e corrigindo o label-content-name-mismatch do Lighthouse.
		>
			<div
				className={cn(
					'w-full gap-8',
					isSearchVariant ? 'flex flex-col md:flex-row-reverse md:items-start md:justify-between' : 'flex flex-col lg:flex-row-reverse',
				)}
			>
				{companyLogo && (
					<Image src={companyLogo} alt="logo" width={100} height={100} className="h-40 w-40 shrink-0 rounded-2xl border border-solid border-base-grey-2 bg-white object-contain p-3" />
				)}
				<div className="mr-auto flex min-w-0 flex-col">
					<p className="text-xs text-brand-main font-bold" aria-label="identificador numérico da vaga">
						#{cod}
					</p>

					<p className={cn('mt-2 font-inter font-bold leading-tight text-gray-900', isSearchVariant ? 'text-2xl md:text-3xl' : 'text-xl')}>{jobTitle}</p>

					<ul className={cn(isSearchVariant ? 'mt-8 mb-6 flex flex-col gap-3 md:flex-row md:flex-wrap md:gap-4' : 'flex flex-col gap-3 mt-4 mb-6')}>
						<li className={metadataItemClassName}>
							<Icon name="location" className="text-xs text-[#0F1DD7]" /> {city ?? 'Remoto'}
						</li>
						{sector && (
							<li className={metadataItemClassName}>
								<Icon name="package" className="text-xs text-[#0F1DD7]" /> {sector}
							</li>
						)}

						<li className={metadataItemClassName}>
							<Icon name="schooling" className="text-xs text-[#0F1DD7]" /> {degreeMap[`${edRequirement}` as keyof typeof degreeMap] ?? edRequirement}
						</li>
						<li className={metadataItemClassName}>
							<Icon name="disability" className="text-xs text-[#0F1DD7]" /> Vaga PCD
						</li>
					</ul>
				</div>
			</div>
			<div className="mt-auto w-full">
				<span className="flex w-full items-center justify-center rounded-full border border-1 border-solid border-[#0F1DD7] py-3 text-sm font-bold text-[#0F1DD7] transition-all group-hover:bg-[#0F1DD7] group-hover:text-white">
					Ver detalhes
				</span>
			</div>
		</Link>
	);
};

export default OpportunityCard;
